/**
 * PostHog client bootstrap for the SPA.
 *
 * Reuses the same project key as the server-side AnalyticsService so SPA
 * and server events land in one project. The public project key (phc_*) is
 * safe to expose client-side.
 *
 * Failure mode: when the key isn't configured or the network is unreachable,
 * posthog.isFeatureEnabled() returns `false`. This is the intentional safe
 * default — every flag fails closed.
 */

import type posthog from "posthog-js";

let initialized = false;
let initFailed = false;
let loadingPromise: Promise<void> | null = null;
let posthogClient: typeof posthog | null = null;
let pendingIdentity: PosthogIdentityInput | null = null;
let featureFlagSubscriptionRegistered = false;
const flagListeners = new Set<() => void>();

export interface PosthogBootstrapInput {
  readonly publicKey: string | null;
  readonly host: string | null;
}

export interface PosthogIdentityInput {
  readonly cmmdUserId: string;
  readonly email?: string;
  readonly displayName?: string;
}

export function bootstrapPosthog(input: PosthogBootstrapInput): void {
  if (initialized || initFailed) return;
  if (!input.publicKey || !input.host) {
    initFailed = true;
    return;
  }
  if (loadingPromise) return;
  if (typeof window === "undefined" || typeof location === "undefined") {
    initFailed = true;
    return;
  }

  const publicKey = input.publicKey;
  const host = input.host;

  loadingPromise = import("posthog-js")
    .then(({ default: client }) => {
      posthogClient = client;
      client.init(publicKey, {
        api_host: host,
        person_profiles: "identified_only",
        // We don't need automatic page-view capture — Sentry already tracks
        // navigation. PostHog is here for flags first; events second.
        capture_pageview: false,
        capture_pageleave: false,
        // Match the server-side telemetry default — only flush when explicitly
        // identified. Anonymous users still get flag evaluation via the
        // bootstrapped key, so this doesn't block the gate logic.
        autocapture: false,
        disable_session_recording: true,
        loaded: () => {
          initialized = true;
          if (pendingIdentity) {
            identifyPosthogUser(pendingIdentity);
          }
          registerFeatureFlagSubscription();
          flagListeners.forEach((listener) => listener());
        },
      });
    })
    .catch(() => {
      initFailed = true;
      posthogClient = null;
    });
}

export function identifyPosthogUser(input: PosthogIdentityInput): void {
  pendingIdentity = input;
  if (!initialized || !posthogClient) return;
  const properties: Record<string, string> = {};
  if (input.email !== undefined) properties.email = input.email;
  if (input.displayName !== undefined) properties.name = input.displayName;
  posthogClient.identify(input.cmmdUserId, properties);
}

export function isPosthogReady(): boolean {
  return initialized;
}

export function getPosthogFeatureFlag(flagKey: string): boolean {
  if (!initialized || !posthogClient) return false;
  return posthogClient.isFeatureEnabled(flagKey) === true;
}

function registerFeatureFlagSubscription(): void {
  if (!posthogClient || featureFlagSubscriptionRegistered) return;
  featureFlagSubscriptionRegistered = true;
  posthogClient.onFeatureFlags(() => {
    flagListeners.forEach((listener) => listener());
  });
}

export function onPosthogFlagsChanged(callback: () => void): () => void {
  flagListeners.add(callback);
  if (initialized && posthogClient) {
    registerFeatureFlagSubscription();
    callback();
  }
  // posthog-js doesn't expose an unsubscribe; the listener lives for the
  // lifetime of the client. Returning a no-op keeps the React effect cleanup
  // semantics consistent.
  return () => {
    flagListeners.delete(callback);
  };
}

export function __resetPosthogForTests(): void {
  initialized = false;
  initFailed = false;
  loadingPromise = null;
  posthogClient = null;
  pendingIdentity = null;
  featureFlagSubscriptionRegistered = false;
  flagListeners.clear();
}
